import React, {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from "react";
import PropTypes from "prop-types";

import { usePlaces } from "./usePlaces";
import {
  DefaultProps,
  Grid,
  Input,
  InputSharedProps,
  InputWrapperBaseProps,
  MantineSize,
  Paper,
  Stack,
  TextInput,
  TextInputStylesNames,
  useInputProps,
  ActionIcon,
  Switch,
  Group,
  SwitchProps,
} from "@mantine/core";

import { IAddress, IAddressModel } from "./types";
import { AddressRightSection } from "./AddressRightSection";
import {
  IconMap,
  IconMapPin,
  IconMapPinOff,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { getAddressObject } from "./utils";
import { UseFormReturnType } from "@mantine/form";

export interface AddressFinderInputProps
  extends DefaultProps<TextInputStylesNames>,
    InputSharedProps,
    InputWrapperBaseProps,
    Omit<
      React.ComponentPropsWithoutRef<"input">,
      "size" | "icon" | "placeholder" | "form" | "defaultValue"
    > {
  /** Props passed to root element (InputWrapper component) */
  wrapperProps?: Record<string, any>;

  /** Input size */
  size?: MantineSize;

  icon?: React.ReactNode;

  noIcon?: boolean;

  placeholder?: string;

  /** Array list of country iso2 codes to restrict the addresses */
  countryRestrictions?: string[];

  /** Allow to clear item */
  clearable?: boolean;

  spacing?: MantineSize;

  addressComponents?: boolean;
  showAddressComponents?: boolean;
  manual?: boolean;
  manualSwitchProps?: SwitchProps;
  addressComponentsLabel?: React.ReactNode;

  apiKey?: string;
  libraries?: string[];
  inputAutocompleteValue?: string;
  options?: any;
  googleMapsScriptBaseUrl?: string;
  language?: string;
  type: "Address" | "City" | "Region" | "Country" | "Any";

  onPlaceSelected?: (place?: google.maps.places.PlaceResult) => void;
  onAddressChange?: (address?: IAddress) => void;

  // form?: UseFormReturnType<Partial<IAddressModel>>;
  // /** parent name e.g. "shippingAddress" */
  // propertyName?: string;

  countryPicker?: ReactNode;
  stateProvincePicker?: ReactNode;
  cityPicker?: ReactNode;

  defaultValue?: IAddressModel;
  __staticSelector?: string;
}

const defaultProps: Partial<AddressFinderInputProps> = {
  size: "sm",
  __staticSelector: "AddressFinderInput",
};

export const AddressFinderInput = forwardRef<
  HTMLInputElement,
  AddressFinderInputProps
>((props, ref) => {
  const {
    inputProps,
    icon,
    noIcon,
    placeholder,
    wrapperProps,
    countryRestrictions = [],
    onAddressChange,
    spacing,
    clearable = true,
    readOnly,
    onPlaceSelected,
    apiKey,
    libraries,
    inputAutocompleteValue,
    options,
    googleMapsScriptBaseUrl,
    language,
    type,
    addressComponents,
    showAddressComponents,
    manual,
    manualSwitchProps,
    addressComponentsLabel,
    // form,
    // propertyName,

    countryPicker,
    stateProvincePicker,
    cityPicker,

    defaultValue,
    ...others
  } = useInputProps("AddressFinderInput", defaultProps, props);

  const [address, setAddress] = useState<Partial<IAddress>>();
  const [placeInternal, setPlaceInternal] =
    useState<google.maps.places.PlaceResult>();
  const [searchValue, setSearchValue] = useState<string>("");

  const onPlceSelectedInternal = useCallback(
    (place?: google.maps.places.PlaceResult) => {
      setPlaceInternal(place);
      if (place && type === "Address") {
        const addr = getAddressObject(place);
        setAddress(addr);

        if (plRef.current && addr.FormattedAddress) {
          plRef.current.value = addr.FormattedAddress;
        }

        onAddressChange?.(addr as IAddress);
      } else {
        setAddress(undefined);
        onAddressChange?.(undefined);
      }
    },
    [type, onAddressChange]
  );

  const { ref: plRef, autocompleteRef } = usePlaces({
    ref: ref,
    googleMapsScriptBaseUrl,
    onPlaceSelected: onPlceSelectedInternal,
    apiKey,
    libraries,
    inputAutocompleteValue,
    options: {
      componentRestrictions: { country: [...countryRestrictions] },
    },
    language,
    type,
  });

  const handleClear = () => {
    if (!readOnly) {
      //   handleChange(null);
      //   if (!controlled) {
      //     handleSearchChange('');
      //   }
      if (plRef.current) {
        plRef.current.value = "";
        setSearchValue("");
        setAddress(undefined);
        onAddressChange?.(undefined);
      }
    }
  };

  useEffect(() => {
    const cur = plRef.current;
    if (cur) {
      cur.addEventListener("change", () => {
        setSearchValue(cur.value ?? "");
      });
    }
    return () => {
      if (cur) {
        cur.removeEventListener("change", () => {});
      }
    };
  });

  useEffect(() => {
    onAddressChange?.(address as any);
  }, [address]);

  return (
    <Stack spacing={spacing}>
      <Input.Wrapper {...wrapperProps}>
        <Input<"input">
          autoComplete="off"
          type="search"
          {...inputProps}
          {...others}
          defaultValue={defaultValue?.FormattedAddress}
          ref={plRef}
          icon={icon}
          placeholder={placeholder ?? "Start typing the address or postcode"}
          rightSectionWidth={100}
          rightSection={
            <Group position="right" w="100%" spacing={0} mr="xs">
              <AddressRightSection
                onClear={handleClear}
                shouldClear={clearable && searchValue !== ""}
              />
            </Group>
          }
        />
      </Input.Wrapper>
    </Stack>
  );
});

AddressFinderInput.propTypes = {
  apiKey: PropTypes.string,
  //libraries: PropTypes.arrayOf(PropTypes.string),
  // ref: PropTypes.oneOfType([
  //   // Either a function
  //   PropTypes.func,
  //   // Or anything shaped { current: any }
  //   PropTypes.shape({ current: PropTypes.any }),
  // ]),
  googleMapsScriptBaseUrl: PropTypes.string,
  onPlaceSelected: PropTypes.func,
  inputAutocompleteValue: PropTypes.string,
  options: PropTypes.shape({
    componentRestrictions: PropTypes.object,
    bounds: PropTypes.object,
    location: PropTypes.object,
    offset: PropTypes.number,
    origin: PropTypes.object,
    radius: PropTypes.number,
    sessionToken: PropTypes.object,
    types: PropTypes.arrayOf(PropTypes.string),
  }),
  language: PropTypes.string,
};

// export default forwardRef((props: any, ref: any) => (
//   <PlacesInput {...props} refProp={ref} />
// ));
